import { Check, X, Star } from "lucide-react";
import { useData } from "../../hooks/useData";
import TiltCard from "../ui-custom/TiltCard";
import GlowButton from "../ui-custom/GlowButton";
import { Reveal } from "../ui-custom/Reveal";

// Pricing cards + dynamic comparison table.
// Per-plan flags supported: hidePrice, comingSoon, contactForPricing.
export default function Pricing({ withComparison = true }) {
  const { data } = useData("pricing");
  const { data: links } = useData("links");
  if (!data) return null;

  const renderPrice = (p) => {
    if (p.comingSoon) return <span className="text-2xl font-bold text-slate-400">Coming Soon</span>;
    if (p.contactForPricing) return <span className="text-2xl font-bold text-slate-900">Contact for Pricing</span>;
    if (p.hidePrice) return null;
    return (
      <>
        <div className="flex items-end gap-1">
          <span className="text-3xl font-bold text-slate-900">{p.activation}</span>
          <span className="mb-1 text-xs text-slate-400">activation</span>
        </div>
        <div className="mt-1 flex gap-4 text-xs text-slate-500">
          <span>Coupon: <b className="text-brand">{p.couponPrice}</b></span>
          <span>Monthly: <b className="text-brand">{p.monthly}</b></span>
        </div>
      </>
    );
  };

  const ctaLabel = (p) => (p.comingSoon ? "Notify Me" : p.contactForPricing ? "Contact Sales" : "Get Started");
  const ctaTarget = (p) =>
    p.comingSoon || p.contactForPricing ? { to: "/contact" } : { href: links?.register, to: !links ? "/contact" : undefined };

  return (
    <div>
      <div className="grid gap-6 lg:grid-cols-4" data-testid="pricing-grid">
        {data.plans.map((p, i) => (
          <Reveal key={p.id} delay={i * 0.08}>
            <TiltCard intensity={5} className={`relative flex h-full flex-col p-7 ${p.recommended ? "grad-border glow-brand" : ""}`} data-testid={`pricing-card-${p.id}`}>
              {p.recommended && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brand-gradient px-3 py-1 text-[11px] font-bold text-white">
                  <Star size={11} className="mr-1 inline" /> RECOMMENDED
                </span>
              )}
              <p className="text-xs uppercase tracking-widest text-brand-2">{p.tier}</p>
              <h3 className="mt-2 text-2xl font-bold text-slate-900">{p.name}</h3>
              <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>

              <div className="mt-5 min-h-[64px]">{renderPrice(p)}</div>

              <ul className="mt-6 flex-1 space-y-3">
                {p.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-slate-600">
                    <Check size={16} className="mt-0.5 shrink-0 text-brand-2" /> {b}
                  </li>
                ))}
              </ul>

              <GlowButton {...ctaTarget(p)} variant={p.recommended ? "primary" : "ghost"} className="mt-7 w-full" data-testid={`pricing-cta-${p.id}`}>
                {ctaLabel(p)}
              </GlowButton>
            </TiltCard>
          </Reveal>
        ))}
      </div>

      {withComparison && (
        <Reveal className="mt-16 overflow-hidden rounded-3xl glass" data-testid="pricing-comparison">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="p-4 font-semibold text-slate-500">Features</th>
                  {Object.keys(data.comparison.matrix).map((name) => (
                    <th key={name} className="p-4 text-center font-semibold text-slate-900">{name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.comparison.features.map((feat, ri) => (
                  <tr key={feat} className="border-b border-slate-100">
                    <td className="p-4 text-slate-600">{feat}</td>
                    {Object.keys(data.comparison.matrix).map((name) => (
                      <td key={name} className="p-4 text-center">
                        {data.comparison.matrix[name][ri] ? (
                          <Check size={18} className="mx-auto text-brand-2" />
                        ) : (
                          <X size={16} className="mx-auto text-slate-300" />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>
      )}
      <p className="mt-6 text-center text-xs text-slate-400">{data.note}</p>
    </div>
  );
}
